import React, { createContext, useContext } from "react";
import branch from "recompose/branch";
import { graphql } from "react-apollo";
import CompanyPermissionsQuery from "./CompanyPermissionsQuery.gql";

/**
 * @callback IsAllowedToFunction
 * @param {string} to
 * @return {boolean}
 */

/**
 * @typedef {object} CompanyPermissionsContextType
 * @property {IsAllowedToFunction} isAllowedTo
 */

const CompanyPermissionsContext = createContext({
  isAllowedTo: () => false,
});

const isAllowedTo = (segments, companyPermissions) => {
  if (!companyPermissions) {
    return false;
  }
  const currentLevel = segments.shift();
  const currentPermission = companyPermissions[currentLevel];
  if (typeof currentPermission === "boolean") {
    return currentPermission;
  }
  return isAllowedTo(segments, currentPermission);
};

const BaseProvider = ({ companyPermissions, children }) => {
  const value = {
    isAllowedTo: (to) => {
      const segments = to.split(".");
      return isAllowedTo(segments, companyPermissions);
    },
  };
  return (
    <CompanyPermissionsContext.Provider value={value}>
      {children}
    </CompanyPermissionsContext.Provider>
  );
};

const Restricted = ({ to, children, fallback = null }) => {
  const { isAllowedTo } = useCompanyPermissions();

  if (isAllowedTo(to)) {
    return <>{children}</>;
  }

  return fallback;
};

/**
 * @returns {CompanyPermissionsContextType}
 */
const useCompanyPermissions = () => {
  return useContext(CompanyPermissionsContext);
};

/**
 * @typedef {Object} CompanyPermissionsQueryGraphQLPropsOptions
 * @property {import('react-apollo').DataValue<{ me: Object }, {}>} data
 * @property {Object} ownProps
 */

/**
 * @typedef {Object} CompanyPermissionsQueryGraphQLExtraProps
 * @property {Object} [companyPermissions]
 */

/**
 * @callback CompanyPermissionsQueryGraphQLPropsCallback
 * @param {CompanyPermissionsQueryGraphQLPropsOptions} param0
 * @returns {CompanyPermissionsQueryGraphQLExtraProps}
 */

const CompanyPermissionsProvider = branch(
  (props) => {
    return !props.companyPermissions && typeof window !== "undefined";
  },
  graphql(CompanyPermissionsQuery, {
    props: /** @type {CompanyPermissionsQueryGraphQLPropsCallback} */ ({
      data,
    }) => {
      return {
        companyPermissions: data.me?.companyPermissions,
      };
    },
  })
)(BaseProvider);

export { CompanyPermissionsProvider, Restricted, useCompanyPermissions };
